<template>
  <div class="all-categories-view">
    <div class="expenses-header">
      <h2>GASTOS</h2>
      <span class="expenses-total">{{ format(totalGastos) }}</span> /
      <span class="expenses-budget">{{ format(totalPresupuesto) }}</span>
    </div>

    <div class="category-actions">
      <b-button
        size="sm"
        variant="outline-primary"
        @click="$bvModal.show('addCategoryModalBudget')"
      >
        + {{ currentParent ? 'Agregar subcategoría' : 'Agregar categoría' }}
      </b-button>
      <b-button
        size="sm"
        :variant="isEditingCategories ? 'outline-secondary' : 'outline-info'"
        @click="isEditingCategories = !isEditingCategories"
      >
        {{ isEditingCategories ? 'Salir edición' : 'Editar categorías' }}
      </b-button>
    </div>
    <div
      v-if="isEditingCategories"
      class="edit-mode-indicator"
    >
      <template v-if="draggedCategory">
        Suelta <strong>{{ draggedCategory.name }}</strong> sobre otra categoría para meterla dentro,
        o sobre un nivel de la ruta para sacarla de aquí.
      </template>
      <template v-else>
        Modo edición activo: arrastra un círculo para moverlo, o usa sus botones
      </template>
    </div>

    <div
      v-if="canGoBack"
      class="category-breadcrumb"
    >
      <b-button
        size="sm"
        variant="outline-secondary"
        class="breadcrumb-back"
        @click="goBack"
      >
        ← Volver
      </b-button>

      <nav class="breadcrumb-trail">
        <button
          type="button"
          class="breadcrumb-crumb"
          :class="{ 'is-drop-target': dropTargetId === 'root' }"
          @click="goToLevel(-1)"
          @dragover.prevent="onCrumbDragOver('root')"
          @dragleave="onDragLeave({ id: 'root' })"
          @drop.prevent="onDropOnCrumb(null)"
        >
          Todas las categorías
        </button>
        <template v-for="(crumb, index) in path">
          <span
            :key="`${crumb.id}-sep`"
            class="breadcrumb-sep"
          >/</span>
          <button
            :key="crumb.id"
            type="button"
            class="breadcrumb-crumb"
            :class="{
              'is-current': index === path.length - 1,
              'is-drop-target': dropTargetId === crumb.id,
            }"
            :disabled="index === path.length - 1 && !draggedCategory"
            @click="goToLevel(index)"
            @dragover.prevent="onCrumbDragOver(crumb.id)"
            @dragleave="onDragLeave(crumb)"
            @drop.prevent="onDropOnCrumb(crumb)"
          >
            {{ crumb.name }}
          </button>
        </template>
      </nav>
    </div>

    <div class="category-tree">
      <div
        v-if="isEmptyEditLevel"
        class="category-empty"
      >
        <div class="category-empty-title">
          Sin subcategorías
        </div>
        <div class="category-empty-sub">
          Agrega una subcategoría con el botón de arriba.
        </div>
      </div>
      <template v-for="level in treeLevels">
        <div
          :key="level.key"
          class="tree-level"
          :class="`tree-level--${level.key}`"
        >
          <div
            v-for="category in level.items"
            :key="category.id"
            class="category-node"
            :class="[
              budgetStatusClass(category),
              isCurrentParent(category) ? 'is-parent' : '',
              isEditingCategories ? 'is-editing' : 'clickable',
              draggedCategory && draggedCategory.id === category.id ? 'is-dragging' : '',
              dropTargetId === category.id ? 'is-drop-target' : ''
            ]"
            :draggable="isEditingCategories"
            @click="handleCategoryClick(category)"
            @dragstart="onDragStart(category)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver(category)"
            @dragleave="onDragLeave(category)"
            @drop.prevent="onDrop(category)"
          >
            <span
              v-if="isEditingCategories"
              class="edit-mode-badge"
            >
              Edición
            </span>

            <div class="category-dial">
              <svg
                class="dial-ring"
                viewBox="0 0 120 120"
              >
                <circle
                  class="dial-track"
                  cx="60"
                  cy="60"
                  r="54"
                />
                <circle
                  v-if="displayBudget(category) > 0"
                  class="dial-value"
                  cx="60"
                  cy="60"
                  r="54"
                  :stroke-dasharray="dialCircumference"
                  :stroke-dashoffset="dialOffset(category)"
                />
              </svg>

              <div class="dial-content">
                <feather-icon
                  :icon="category.icon || 'FolderIcon'"
                  class="category-icon"
                  size="16"
                />
                <div
                  class="category-cost"
                  :style="{ fontSize: costFontSize(category) }"
                >
                  {{ format(displayCost(category)) }}
                </div>
                <div class="category-budget">
                  <template v-if="displayBudget(category) > 0">
                    de {{ format(displayBudget(category)) }}
                  </template>
                  <template v-else>
                    sin presupuesto
                  </template>
                </div>
                <div
                  v-if="displayBudget(category) > 0"
                  class="category-percentage"
                >
                  {{ getExecutionPercentage(category) }}%
                </div>
              </div>
            </div>

            <div class="node-info">
              <div
                class="category-name"
                :title="category.name || '-'"
              >
                {{ category.name || '-' }}
              </div>
              <div class="category-meta">
                <span
                  v-if="isCurrentParent(category)"
                  v-b-tooltip.hover
                  class="meta-tag"
                  title="Montos propios de esta categoría. Sus subcategorías se muestran por separado."
                >
                  Categoría actual
                </span>
                <span v-else>{{ metaLabel(category) }}</span>
              </div>
            </div>

            <div class="node-actions">
              <template v-if="isEditingCategories">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="node-action-button"
                  @click.stop="editCategory(category)"
                >
                  Editar categoría
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="node-action-button"
                  @click.stop="handleMoveCategory(category)"
                >
                  Mover
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="node-action-button"
                  @click.stop="handleDeleteCategory(category)"
                >
                  Eliminar
                </b-button>
              </template>
              <b-button
                v-else
                size="sm"
                variant="outline-primary"
                class="node-action-button"
                @click.stop="editBudget(category)"
              >
                Editar presupuesto
              </b-button>
            </div>
              </div>
            </div>
        <div
          v-if="level.key === 'parent'"
          :key="`${level.key}-stem`"
          class="tree-stem"
        />
      </template>

      <!-- Modal para editar presupuesto -->
      <b-modal
        v-model="showEditModal"
        title="Editar presupuesto"
        @ok="saveBudget"
      >
        <div>
          <label>
            Nuevo presupuesto para
            <strong>{{ editingCategory ? editingCategory.name : '-' }}</strong>:
          </label>
          <b-form-input
            v-model.number="editedBudget"
            type="number"
            min="0"
            step="0.01"
          />
        </div>
      </b-modal>
    </div>

    <add-category-modal
      id="addCategoryModalBudget"
      :project-id="$route.params.projectId"
      :parent-id="currentParent ? currentParent.id : ''"
      @refetch="refreshCategories"
      @update="refreshCategories"
    />
    <edit-category-modal
      v-if="categoryToEdit"
      :key="categoryToEdit.id"
      :project-id="$route.params.projectId"
      :parent-id="categoryEditParentId"
      :category="categoryToEdit"
      @update="refreshCategories"
      @refetch="refreshCategories"
    />
    <move-category-modal
      v-if="categoryToMove"
      :key="`move-${categoryToMove.id}`"
      :project-id="$route.params.projectId"
      :category="categoryToMove"
      :categories="categories"
      :current-parent-id="moveCurrentParentId"
      @moved="handleCategoryMoved"
    />
    <confirmation-modal
      :id="'confirmDeleteBudgetCategory'"
      :variant="'danger'"
      @confirm="removeCategory"
    >
      <template v-slot:content>
        {{ $t('deleteExpenseTypeConfirmation', {
          expenseType: categoryToDelete ? categoryToDelete.name : ''
        }) }}
      </template>
    </confirmation-modal>
  </div></template>

<script>
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import AddCategoryModal from './AddCategoryModal.vue'
import EditCategoryModal from './EditCategoryModal.vue'
import MoveCategoryModal from './MoveCategoryModal.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

export default {
  components: {
    FeatherIcon,
    AddCategoryModal,
    EditCategoryModal,
    MoveCategoryModal,
    ConfirmationModal,
  },
  data() {
    return {
      categories: [],
      totalGastos: 0,
      totalPresupuesto: 0,
      path: [],
      showEditModal: false,
      editingCategory: null,
      editedBudget: null,
      categoryToEdit: null,
      categoryEditParentId: '',
      categoryToDelete: null,
      categoryToMove: null,
      moveCurrentParentId: '',
      draggedCategory: null,
      dropTargetId: null,
      isEditingCategories: false,
    }
  },
  computed: {
    canGoBack() {
      return this.path.length > 0
    },
    displayedCategories() {
      if (!this.canGoBack) return this.categories
      const parent = this.path[this.path.length - 1]
      const subs = parent.subcategories || parent.children || []
      if (this.isEditingCategories) return subs
      return [parent, ...subs]
    },
    isEmptyEditLevel() {
      if (!this.isEditingCategories || !this.canGoBack) return false
      const parent = this.path[this.path.length - 1]
      const subs = parent?.subcategories || parent?.children || []
      return subs.length === 0
    },
    currentTree() {
      if (!this.canGoBack) return this.categories
      return [this.path[this.path.length - 1]]
    },
    currentParent() {
      return this.path[this.path.length - 1] || null
    },
    treeLevels() {
      if (!this.canGoBack || this.isEditingCategories) {
        return [{ key: 'roots', items: this.displayedCategories }]
      }

      const parent = this.currentParent
      const subs = parent.subcategories || parent.children || []
      return [
        { key: 'parent', items: [parent] },
        { key: 'children', items: subs },
      ]
    },
    dialCircumference() {
      return 2 * Math.PI * 54
    },
  },
  watch: {
    currentTree: {
      immediate: true,
      handler(tree) {
        this.calculateTotals(tree)
      },
    },
  },
  created() {
    this.fetchCategories()
  },
  methods: {
    ...mapActions('groupedExpenses', [
      'fetchExpensesTypeCatalogs',
      'updateExpenseTypeBudget',
      'deleteExpensesTypesCatalog',
      'moveExpenseTypeCatalog',
    ]),
    handleCategoryClick(category) {
      if (this.isEditingCategories) {
        this.navigateToEdit(category)
        return
      }
      this.navigateTo(category)
    },
    navigateToEdit(category) {
      const alreadyInPath = this.path.some(p => p.id === category.id)
      if (!alreadyInPath) {
        this.path.push(category)
      }
    },
    navigateTo(category) {
      const subs = category.subcategories || category.children || []
      const alreadyInPath = this.path.some(p => p.id === category.id)

      if (subs.length && !alreadyInPath) {
        // Si tiene subcategorías y no está ya en el path, baja un nivel
        this.path.push(category)
      } else {
        // Redirige si ya no hay más subcategorías o ya estamos dentro de esta categoría
        const { projectId } = this.$route.params
        this.$router.push({
          name: 'allExpenses',
          params: { projectId },
          query: {
            expensetypecatalog: category.id,
          },
        })
      }
    },
    goBack() {
      this.path.pop()
    },
    goToLevel(index) {
      this.path = index < 0 ? [] : this.path.slice(0, index + 1)
    },
    onDragStart(category) {
      if (!this.isEditingCategories) return
      this.draggedCategory = category
    },
    onDragEnd() {
      this.draggedCategory = null
      this.dropTargetId = null
    },
    canDropOn(category) {
      if (!this.draggedCategory) return false
      if (category.id === this.draggedCategory.id) return false
      // Soltar dentro de la propia descendencia dejaria el arbol en ciclo.
      return !this.flattenList([this.draggedCategory]).some(c => c.id === category.id)
    },
    onDragOver(category) {
      if (!this.canDropOn(category)) return
      this.dropTargetId = category.id
    },
    onCrumbDragOver(crumbId) {
      if (!this.draggedCategory) return
      if (crumbId === this.draggedCategory.id) return
      this.dropTargetId = crumbId
    },
    onDragLeave(category) {
      if (this.dropTargetId === category.id) this.dropTargetId = null
    },
    onDrop(category) {
      if (!this.canDropOn(category)) return
      this.moveCategoryTo(this.draggedCategory, category.id)
    },
    onDropOnCrumb(crumb) {
      if (!this.draggedCategory) return
      if (crumb && crumb.id === this.draggedCategory.id) return
      this.moveCategoryTo(this.draggedCategory, crumb ? crumb.id : '')
    },
    moveCategoryTo(category, parentId) {
      const { projectId } = this.$route.params
      const { name } = category

      this.draggedCategory = null
      this.dropTargetId = null

      return this.moveExpenseTypeCatalog({ projectId, categoryId: category.id, parentId })
        .then(() => {
          this.path = this.path.filter(p => p.id !== category.id)
          return this.refreshCategories()
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${name} se movió`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          const msg = error?.data?.messages?.[0] || 'No se pudo mover la categoría.'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        })
    },
    handleMoveCategory(category) {
      this.categoryToMove = category
      this.moveCurrentParentId = this.findParentIdByChildId(this.categories, category.id) || ''
      this.$nextTick(() => this.$bvModal.show('moveCategoryModal'))
    },
    handleCategoryMoved() {
      const movedId = this.categoryToMove ? this.categoryToMove.id : null
      this.categoryToMove = null
      // Si la categoria movida estaba en la ruta, el nivel donde estabas dejo de existir.
      if (movedId) this.path = this.path.filter(p => p.id !== movedId)
      this.refreshCategories()
    },
    fetchCategories() {
      return this.refreshCategories()
    },
    refreshCategories() {
      const { projectId } = this.$route.params
      return this.fetchExpensesTypeCatalogs(projectId).then(resp => {
        this.categories = resp.data
        if (this.path.length) {
          this.path = this.path
            .map(p => this.findCategoryById(resp.data, p.id))
            .filter(Boolean)
        }
        this.calculateTotals(this.currentTree)
        return resp
      })
    },
    flattenList(list) {
      return list.reduce((acc, node) => {
        acc.push(node)
        const subs = node.subcategories || node.children || []
        if (subs.length) {
          acc = acc.concat(this.flattenList(subs))
        }
        return acc
      }, [])
    },
    sumCostTree(cat) {
      const total = this.flattenList([cat])
        .reduce((s, c) => s + (parseFloat(c.cost) || 0), 0)
      return Math.round((total + Number.EPSILON) * 100) / 100
    },
    sumBudgetTree(cat) {
      const total = this.flattenList([cat])
        .reduce((s, c) => s + (parseFloat(c.budget) || 0), 0)
      return Math.round((total + Number.EPSILON) * 100) / 100
    },
    calculateTotals(tree) {
      const nodes = this.flattenList(tree)

      this.totalGastos = nodes
        .reduce((sum, c) => sum + (parseFloat(c.cost) || 0), 0)

      this.totalPresupuesto = nodes
        .reduce((sum, c) => sum + (parseFloat(c.budget) || 0), 0)
    },
    format(value) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(value)
    },
    isCurrentParent(category) {
      return Boolean(this.currentParent) && category.id === this.currentParent.id
    },
    subcategoryCount(category) {
      const subs = category.subcategories || category.children || []
      return subs.length
    },
    displayCost(category) {
      if (this.isCurrentParent(category)) return parseFloat(category.cost) || 0
      return this.sumCostTree(category)
    },
    displayBudget(category) {
      if (this.isCurrentParent(category)) return parseFloat(category.budget) || 0
      return this.sumBudgetTree(category)
    },
    getExecutionPercentage(category) {
      const budget = this.displayBudget(category)
      if (!budget) return 0
      return Math.round((this.displayCost(category) / budget) * 100)
    },
    dialOffset(category) {
      const percent = Math.min(this.getExecutionPercentage(category), 100)
      return this.dialCircumference * (1 - percent / 100)
    },
    costFontSize(category) {
      const { length } = this.format(this.displayCost(category))
      if (length <= 11) return '17px'
      if (length <= 14) return '15px'
      return '13px'
    },
    metaLabel(category) {
      if (this.budgetStatusClass(category) === 'over-budget') return this.remainingLabel(category)

      const subs = this.subcategoryCount(category)
      if (subs) return `${subs} ${subs === 1 ? 'subcategoría' : 'subcategorías'}`

      return this.remainingLabel(category) || 'Sin subcategorías'
    },
    remainingLabel(category) {
      const budget = this.displayBudget(category)
      if (!budget) return ''

      const diff = Math.round((budget - this.displayCost(category) + Number.EPSILON) * 100) / 100
      if (diff < 0) return `Excedido ${this.format(Math.abs(diff))}`
      return `Restan ${this.format(diff)}`
    },
    budgetStatusClass(category) {
      if (!this.displayBudget(category)) return 'no-budget'

      const percent = this.getExecutionPercentage(category)
      if (percent > 110) return 'over-budget'
      if (percent >= 95) return 'near-budget'
      return 'within-budget'
    },
    editBudget(category) {
      this.editingCategory = category
      this.editedBudget = parseFloat(category.budget) || 0
      this.showEditModal = true
    },
    async editCategory(category) {
      this.categoryToEdit = category
      this.categoryEditParentId = this.findParentIdByChildId(this.categories, category.id) || ''
      await this.$nextTick()
      this.$bvModal.show('editCategoryModal')
    },
    handleDeleteCategory(category) {
      this.categoryToDelete = category
      this.$bvModal.show('confirmDeleteBudgetCategory')
    },
    removeCategory() {
      if (!this.categoryToDelete) return

      const { projectId } = this.$route.params
      this.deleteExpensesTypesCatalog({
        projectId,
        expenseTypeId: this.categoryToDelete.id,
      }).then(() => {
        this.refreshCategories()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('expenseTypeDeleted'),
            icon: 'SendIcon',
            variant: 'success',
          },
        })
      })
    },
    saveBudget() {
      if (this.editingCategory && this.editedBudget != null) {
        const { projectId } = this.$route.params
        const categoryId = this.editingCategory.id

        this.updateExpenseTypeBudget({
          projectId,
          categoryId,
          budget: this.editedBudget,
        })
          .then(() => this.refreshCategories())
          .then(() => {
            this.showEditModal = false

            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('budgetUpdated'),
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          })
          .catch(error => {
            const msg = error?.data?.messages?.[0] || this.$t('budgetUpdateFailed')
            this.$toast({
              component: ToastificationContent,
              props: {
                title: msg,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          })
      }
    },
    findCategoryById(list, id) {
      let result = null

      list.some(item => {
        if (item.id === id) {
          result = item
          return true
        }

        const subs = item.subcategories || item.children || []
        const found = this.findCategoryById(subs, id)
        if (found) {
          result = found
          return true
        }

        return false
      })

      return result
    },
    findParentIdByChildId(list, childId, parentId = '') {
      let found = null
      list.some(item => {
        if (item.id === childId) {
          found = parentId || ''
          return true
        }

        const subs = item.subcategories || item.children || []
        if (subs.length) {
          const result = this.findParentIdByChildId(subs, childId, item.id)
          if (result !== null) {
            found = result
            return true
          }
        }

        return false
      })

      return found
    },
  },
}
</script>

<style scoped>
.all-categories-view {
  --yoc-brand: #7c127b;
  --yoc-surface: #fff;
  --yoc-border: #e6e8ee;
  --yoc-text: #2f3349;
  --yoc-muted: #8a8f9c;
  --yoc-track: #eceef4;
  --yoc-success: #28c76f;
  --yoc-warning: #ff9f43;
  --yoc-danger: #ea5455;

  padding: 24px 20px 36px;
}

body.dark-layout .all-categories-view {
  --yoc-brand: #c885c7;
  --yoc-surface: #283046;
  --yoc-border: #3b4253;
  --yoc-text: #d0d2d6;
  --yoc-muted: #8b8fa3;
  --yoc-track: #3b4253;
}

.expenses-header {
  text-align: center;
  margin-bottom: 18px;
}

.expenses-header h2 {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--yoc-muted);
  margin-bottom: 6px;
}

.expenses-total {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--yoc-brand);
}

.expenses-budget {
  font-size: 17px;
  font-weight: 500;
  color: var(--yoc-muted);
}

.category-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.category-actions .btn {
  border-radius: 20px;
  padding: 5px 16px;
  font-size: 12px;
  font-weight: 500;
}

.edit-mode-indicator {
  text-align: center;
  font-size: 12px;
  color: var(--yoc-muted);
  margin-bottom: 10px;
}

.category-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-bottom: 4px;
}

.breadcrumb-back.btn {
  border-radius: 20px;
  font-size: 12px;
}

.breadcrumb-trail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 12px;
  min-width: 0;
}

.breadcrumb-crumb {
  padding: 2px 6px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--yoc-brand);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breadcrumb-crumb:hover {
  background-color: rgba(124, 18, 123, 0.08);
}

.breadcrumb-crumb.is-current {
  color: var(--yoc-text);
  font-weight: 600;
  cursor: default;
}

.breadcrumb-crumb.is-current:hover {
  background: none;
}

.breadcrumb-sep {
  color: var(--yoc-muted);
}

body.dark-layout .breadcrumb-crumb:hover {
  background-color: rgba(200, 133, 199, 0.14);
}

.category-tree {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.tree-level {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 32px 26px;
  width: 100%;
}

/* Tronco que baja de la categoría padre hacia sus subcategorías */
.tree-stem {
  width: 2px;
  height: 30px;
  background-color: var(--yoc-border);
}

/* Peine que reparte el tronco entre las subcategorías */
.tree-level--children {
  gap: 46px 26px;
}

.tree-level--children > .category-node {
  padding-top: 30px;
}

.tree-level--children > .category-node::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 30px;
  background-color: var(--yoc-border);
}

.tree-level--children > .category-node::after {
  content: '';
  position: absolute;
  top: 0;
  left: -14px;
  right: -14px;
  height: 2px;
  background-color: var(--yoc-border);
}

.tree-level--children > .category-node:first-child::after {
  left: 50%;
}

.tree-level--children > .category-node:last-child::after {
  right: 50%;
}

.category-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 196px;
  padding-top: 8px;
}

.category-node.clickable {
  cursor: pointer;
}

.category-node.is-editing {
  cursor: grab;
}

.category-node.is-dragging {
  opacity: 0.4;
}

.category-node.is-drop-target .category-dial {
  box-shadow: 0 0 0 3px var(--yoc-brand), 0 10px 22px rgba(34, 41, 47, 0.16);
  transform: translateY(-4px);
}

.breadcrumb-crumb.is-drop-target {
  background-color: rgba(124, 18, 123, 0.16);
  outline: 2px dashed var(--yoc-brand);
}

/* ---- Aro de progreso ---- */

.category-dial {
  position: relative;
  width: 176px;
  height: 176px;
  border-radius: 50%;
  background-color: var(--yoc-surface);
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.category-node.clickable:hover .category-dial {
  transform: translateY(-4px);
  box-shadow: 0 10px 22px rgba(34, 41, 47, 0.16);
}

.dial-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.dial-track,
.dial-value {
  fill: none;
  stroke-width: 8;
}

.dial-track {
  stroke: var(--yoc-track);
}

.dial-value {
  stroke: var(--yoc-brand);
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease;
}

.category-node.within-budget .dial-value {
  stroke: var(--yoc-success);
}

.category-node.near-budget .dial-value {
  stroke: var(--yoc-warning);
}

.category-node.over-budget .dial-value {
  stroke: var(--yoc-danger);
}

.category-node.is-parent .category-dial {
  box-shadow: 0 0 0 1px var(--yoc-brand), 0 2px 10px rgba(34, 41, 47, 0.08);
}

.dial-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 0 24px;
  text-align: center;
}

.category-icon {
  color: var(--yoc-brand);
  margin-bottom: 4px;
}

.category-cost {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--yoc-text);
  white-space: nowrap;
}

.category-budget {
  font-size: 10px;
  line-height: 1.3;
  color: var(--yoc-muted);
}

.category-percentage {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  color: var(--yoc-muted);
}

.category-node.within-budget .category-percentage {
  color: var(--yoc-success);
}

.category-node.near-budget .category-percentage {
  color: var(--yoc-warning);
}

.category-node.over-budget .category-percentage {
  color: var(--yoc-danger);
}

/* ---- Etiqueta bajo el aro ---- */

.node-info {
  margin-top: 14px;
  text-align: center;
  width: 100%;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--yoc-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.category-meta {
  margin-top: 2px;
  font-size: 11px;
  color: var(--yoc-muted);
}

.category-node.over-budget .category-meta {
  color: var(--yoc-danger);
  font-weight: 600;
}

.meta-tag {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 20px;
  font-weight: 600;
  color: var(--yoc-brand);
  background-color: rgba(124, 18, 123, 0.1);
  cursor: help;
}

body.dark-layout .meta-tag {
  background-color: rgba(200, 133, 199, 0.16);
}

.node-actions {
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.node-action-button {
  width: 168px;
  max-width: 100%;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  white-space: nowrap;
}

/* El primary del tema (#340668) es ilegible sobre el fondo oscuro */
body.dark-layout .category-actions .btn-outline-primary,
body.dark-layout .node-action-button.btn-outline-primary {
  color: var(--yoc-brand);
  border-color: var(--yoc-brand);
}

body.dark-layout .category-actions .btn-outline-primary:hover,
body.dark-layout .node-action-button.btn-outline-primary:hover {
  color: #fff;
  background-color: var(--yoc-brand);
}

.edit-mode-badge {
  position: absolute;
  top: 0;
  right: 8px;
  z-index: 2;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--yoc-muted);
  background-color: var(--yoc-surface);
  border: 1px dashed var(--yoc-muted);
}

.category-empty {
  width: 100%;
  max-width: 360px;
  text-align: center;
  color: var(--yoc-muted);
  border: 1px dashed var(--yoc-border);
  border-radius: 12px;
  padding: 24px 16px;
}

.category-empty-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.category-empty-sub {
  font-size: 12px;
}

@media (max-width: 575.98px) {
  .all-categories-view {
    padding: 16px 12px 24px;
  }

  .tree-level {
    gap: 26px 16px;
  }

  .tree-level--children > .category-node::after {
    left: -9px;
    right: -9px;
  }

  .expenses-total {
    font-size: 26px;
  }
}
</style>
